import React, { useMemo, useState, useCallback } from 'react'
import {
    BarChart,
    Bar,
    XAxis,
    YAxis,
    CartesianGrid,
    ResponsiveContainer,
    Tooltip,
    LabelList,
} from 'recharts'
import { Toggle } from 'components'
import { analyticsColorPalette } from 'app/analytics'
import { useLocalStorage } from 'app/hooks'
import { AnalyticsChartWrapper } from './AnalyticsChartWrapper'
import { CACBenchmarkLegend, FunnelLegendRow, FunnelStageKey, FUNNEL_STAGE_SUBTITLES, getMarketReferenceRate } from './CACBenchmarkLegend'

interface StackedFunnelDataRow {
    source?: string
    campaign?: string
    latestVisitCount?: number
    latestRegistrationCount?: number
    initialRegistrationCount?: number
    latestMeetingCount?: number
    initialMeetingCount?: number
    latestSaleCount?: number
    initialSaleCount?: number
    visitCount?: number
    registrationCount?: number
    meetingCount?: number
    saleCount?: number
}

interface AnalyticsStackedFunnelChartProps {
    data: StackedFunnelDataRow[]
    transitioning?: boolean
    marginModifier?: number
    aggregation?: string
    onPointClick?: (payload: {
        chartType: string
        labelKey: string
        labelValue: string | number
        row: any
    }) => void
}

type SourceMode = 'latest' | 'initial'
type DataMode = 'by_source' | 'by_campaign'

// The four funnel stages - ordered so they display top-to-bottom as: visits → registrations → meetings → sales
const STAGES = [
    { key: 'visit', label: 'Visits', color: '#3498db' },
    { key: 'registration', label: 'Registrations', color: '#27ae60' },
    { key: 'meeting', label: 'Meetings', color: '#e67e22' },
    { key: 'sale', label: 'Sales', color: '#e74c3c' },
] as const

/**
 * Detect data mode from the shape of the first row
 */
function detectDataMode(data: StackedFunnelDataRow[]): DataMode {
    if (!data || data.length === 0) return 'by_source'
    const first = data[0]
    if ('campaign' in first || 'visitCount' in first) return 'by_campaign'
    return 'by_source'
}

/**
 * Build the stacked bar data:
 * Each bar = one funnel stage
 * Each segment in the bar = one source/campaign
 * Value = count for that entity at that stage
 */
function buildStackedData(
    data: StackedFunnelDataRow[],
    sourceMode: SourceMode,
    dataMode: DataMode,
    hiddenEntities: Set<string>,
): { chartData: any[]; entities: string[] } {
    const entitySet = new Set<string>()

    const getEntityKey = (row: StackedFunnelDataRow): string => {
        if (dataMode === 'by_campaign') return row.campaign || 'no_campaign'
        return row.source || 'unknown'
    }

    const getFieldKey = (stageKey: string, mode: SourceMode): string => {
        if (dataMode === 'by_campaign') {
            // Campaign mode: visit → visitCount, registration → registrationCount, etc.
            return `${stageKey}Count`
        }
        // Source mode uses latest/initial prefixes
        if (stageKey === 'visit') return 'latestVisitCount'
        const prefix = mode === 'latest' ? 'latest' : 'initial'
        const capitalized = stageKey.charAt(0).toUpperCase() + stageKey.slice(1)
        return `${prefix}${capitalized}Count`
    }

    // First pass: find all entities that have non-zero data
    data.forEach((row) => {
        let hasValue = false
        STAGES.forEach((stage) => {
            const fieldKey = getFieldKey(stage.key, sourceMode)
            if (Number(row[fieldKey as keyof StackedFunnelDataRow] ?? 0) > 0) {
                hasValue = true
            }
        })
        if (hasValue) {
            entitySet.add(getEntityKey(row))
        }
    })

    const entities = Array.from(entitySet).sort((a, b) => {
        const totalA = STAGES.reduce((sum, stage) => {
            const fieldKey = getFieldKey(stage.key, sourceMode)
            const rowA = data.find((r) => getEntityKey(r) === a)
            return sum + Number(rowA?.[fieldKey as keyof StackedFunnelDataRow] ?? 0)
        }, 0)
        const totalB = STAGES.reduce((sum, stage) => {
            const fieldKey = getFieldKey(stage.key, sourceMode)
            const rowB = data.find((r) => getEntityKey(r) === b)
            return sum + Number(rowB?.[fieldKey as keyof StackedFunnelDataRow] ?? 0)
        }, 0)
        return totalB - totalA
    })

    // Filter out hidden entities from chart data but keep them in the entities list
    const visibleEntities = entities.filter((e) => !hiddenEntities.has(e))

    // Build chart data: one entry per funnel stage
    const chartData = STAGES.map((stage) => {
        const entry: any = {
            stage: stage.label,
            stageColor: stage.color,
        }
        let total = 0
        visibleEntities.forEach((entity) => {
            const row = data.find((r) => getEntityKey(r) === entity)
            const fieldKey = getFieldKey(stage.key, sourceMode)
            const val = Number(row?.[fieldKey as keyof StackedFunnelDataRow] ?? 0)
            entry[entity] = val
            total += val
        })
        entry._total = total
        return entry
    })

    return { chartData, entities: visibleEntities }
}

// Assign deterministic colors to entities
function getEntityColors(entities: string[]): Record<string, string> {
    const colors: Record<string, string> = {}
    entities.forEach((entity, i) => {
        colors[entity] = analyticsColorPalette[i % analyticsColorPalette.length]
    })
    return colors
}

const CustomTooltip = ({ active, payload, label, hoveredEntity }: any) => {
    if (!active || !payload || payload.length === 0) return null

    // Get the stage total from the data object (sum of all visible sources)
    const stageTotal = payload[0]?.payload?._originalTotal || 0
    const rawValues = payload[0]?.payload?._rawValues || {}

    // Sort tooltip entries by raw count descending, but keep hovered entity on top
    const sorted = [...payload].sort((a: any, b: any) => {
        if (hoveredEntity) {
            if (a.name === hoveredEntity) return -1
            if (b.name === hoveredEntity) return 1
        }
        const countA = rawValues[a.name] || 0
        const countB = rawValues[b.name] || 0
        return countB - countA
    })

    return (
        <div className="analytics-tooltip analytics-stacked-funnel-tooltip">
            <p className="analytics-tooltip-label">{label}</p>
            <p className="analytics-tooltip-total">Total: {stageTotal.toLocaleString()}</p>
            <div className="analytics-tooltip-items">
                {sorted.map((entry: any) => {
                    const count = rawValues[entry.name] || 0
                    const pct = stageTotal > 0 ? (count / stageTotal) * 100 : 0
                    return (
                        <div
                            key={entry.name}
                            className="analytics-tooltip-item"
                            style={{
                                opacity: hoveredEntity !== null && entry.name !== hoveredEntity ? 0.65 : 1,
                                transition: 'opacity 0.15s ease',
                            }}
                        >
                            <span
                                className="analytics-tooltip-dot"
                                style={{ backgroundColor: entry.fill || entry.color }}
                            />
                            <span className="analytics-tooltip-item-name">{entry.name}</span>
                            <span className="analytics-tooltip-item-value">
                                {count.toLocaleString()}
                                {stageTotal > 0 && (
                                    <span className="analytics-tooltip-item-pct">
                                        {' '}({pct.toFixed(1)}%)
                                    </span>
                                )}
                            </span>
                        </div>
                    )
                })}
            </div>
        </div>
    )
}

export function AnalyticsStackedFunnelChart(props: AnalyticsStackedFunnelChartProps) {
    const { data, transitioning, marginModifier = 1.0, aggregation, onPointClick } = props
    const densityClass = marginModifier <= 0.4
        ? 'density-quarter'
        : marginModifier <= 0.75
            ? 'density-half'
            : 'density-full'
    const isQuarterDensity = marginModifier <= 0.4
    const isHalfDensity = marginModifier > 0.4 && marginModifier <= 0.75
    const [sourceMode, setSourceMode] = useState<SourceMode>('latest')
    const [hoveredEntity, setHoveredEntity] = useState<string | null>(null)
    
    // Create a unique storage key per analytics aggregation
    const storageKey = aggregation ? `analytics-funnel-hidden-entities-${aggregation}` : null
    const [storedHiddenEntities, setStoredHiddenEntities] = useLocalStorage(storageKey || 'analytics-funnel-hidden-entities', [])
    // Convert stored value back to Set - localStorage stores Sets as [], so convert array to Set
    const hiddenEntities = new Set(Array.isArray(storedHiddenEntities) ? storedHiddenEntities : (storedHiddenEntities instanceof Set ? Array.from(storedHiddenEntities) : []))

    const dataMode = useMemo(() => detectDataMode(data), [data])

    const getFieldKey = useCallback((stageKey: string, mode: SourceMode): string => {
        if (dataMode === 'by_campaign') return `${stageKey}Count`
        if (stageKey === 'visit') return 'latestVisitCount'
        const prefix = mode === 'latest' ? 'latest' : 'initial'
        const capitalized = stageKey.charAt(0).toUpperCase() + stageKey.slice(1)
        return `${prefix}${capitalized}Count`
    }, [dataMode])

    // Get all entities relevant to the current sourceMode, sorted by total count descending
    const allEntities = useMemo(() => {
        const entityTotals = new Map<string, number>()
        data.forEach((row) => {
            const key = dataMode === 'by_campaign' ? (row.campaign || 'no_campaign') : (row.source || 'unknown')
            const total = STAGES.reduce((sum, stage) => {
                const fieldKey = getFieldKey(stage.key, sourceMode)
                return sum + Number(row[fieldKey as keyof StackedFunnelDataRow] ?? 0)
            }, 0)
            if (total > 0) {
                entityTotals.set(key, (entityTotals.get(key) ?? 0) + total)
            }
        })
        return Array.from(entityTotals.entries())
            .sort((a, b) => b[1] - a[1])
            .map(([key]) => key)
    }, [data, dataMode, sourceMode, getFieldKey])

    // Entity totals map for displaying counts in the filter
    const entityTotals = useMemo(() => {
        const totals = new Map<string, number>()
        data.forEach((row) => {
            const key = dataMode === 'by_campaign' ? (row.campaign || 'no_campaign') : (row.source || 'unknown')
            const total = STAGES.reduce((sum, stage) => {
                const fieldKey = getFieldKey(stage.key, sourceMode)
                return sum + Number(row[fieldKey as keyof StackedFunnelDataRow] ?? 0)
            }, 0)
            if (total > 0) {
                totals.set(key, (totals.get(key) ?? 0) + total)
            }
        })
        return totals
    }, [data, dataMode, sourceMode, getFieldKey])

    const { chartData: baseChartData, entities } = useMemo(
        () => buildStackedData(data, sourceMode, dataMode, hiddenEntities),
        [data, sourceMode, dataMode, hiddenEntities],
    )

    const toggleEntity = useCallback((entity: string) => {
        setStoredHiddenEntities((prev) => {
            const prevArray = Array.isArray(prev) ? prev : []
            const hiddenSet = new Set(prevArray)

            const allVisible = hiddenSet.size === 0

            if (allVisible) {
                // First click when everything is visible → isolate (solo) this entity
                return allEntities.filter((e) => e !== entity)
            }

            // Clicking the sole visible entity → show all
            const isAlreadySolo = hiddenSet.size === allEntities.length - 1 && !hiddenSet.has(entity)
            if (isAlreadySolo) {
                return []
            }

            // Otherwise just toggle this entity on/off
            const next = new Set(hiddenSet)
            if (next.has(entity)) {
                next.delete(entity)
            } else {
                next.add(entity)
            }
            return Array.from(next)
        })
    }, [allEntities])

    const showAllEntities = useCallback(() => {
        setStoredHiddenEntities([])
    }, [])

    const hideAllEntities = useCallback(() => {
        // Hide all but keep one visible to prevent empty chart
        const allButFirst = Array.from(allEntities.slice(1))
        setStoredHiddenEntities(allButFirst)
    }, [allEntities])

    // Add conversion rate labels to chart data for display
    const chartData = useMemo(() => {
        return baseChartData.map((entry, i) => {
            const withLabels: any = {
                ...entry,
                _originalTotal: entry._total,
                _rawValues: {},
            }

            let normalizedTotal = 0
            entities.forEach((entity, entityIndex) => {
                const rawValue = Number(entry[entity] ?? 0)
                withLabels._rawValues[entity] = rawValue

                if (entry._total <= 0) {
                    withLabels[entity] = 0
                    return
                }

                if (entityIndex === entities.length - 1) {
                    withLabels[entity] = Math.max(0, 100 - normalizedTotal)
                    return
                }

                const normalizedValue = (rawValue / entry._total) * 100
                withLabels[entity] = normalizedValue
                normalizedTotal += normalizedValue
            })

            // Calculate conversion rate from previous stage
            if (i === 0) {
                withLabels._conversionLabel = ''
            } else {
                const prev = baseChartData[i - 1]._total
                const curr = entry._total
                const rate = prev > 0 ? (curr / prev) * 100 : 0
                withLabels._conversionLabel = (Math.round(rate * 10) / 10).toFixed(1) + '%'
            }
            return withLabels
        })
    }, [baseChartData, entities])

    const entityColors = useMemo(() => getEntityColors(allEntities), [allEntities])

    const conversionRates = useMemo(() => {
        const stageRates: Partial<Record<FunnelStageKey, string>> = {}
        chartData.forEach((entry) => {
            if (entry.stage === 'Registrations') {
                stageRates.registration = entry._conversionLabel || '—'
            } else if (entry.stage === 'Meetings') {
                stageRates.meeting = entry._conversionLabel || '—'
            } else if (entry.stage === 'Sales') {
                stageRates.sale = entry._conversionLabel || '—'
            } else if (entry.stage === 'Visits') {
                stageRates.visit = '—'
            }
        })
        return stageRates
    }, [chartData])

    const legendRows: FunnelLegendRow[] = useMemo(() => {
        const keyByStage: Record<string, FunnelStageKey> = {
            Visits: 'visit',
            Registrations: 'registration',
            Meetings: 'meeting',
            Sales: 'sale',
        }

        return chartData.filter((entry) => entry.stage !== 'Visits').map((entry) => {
            const key = keyByStage[entry.stage] || 'visit'
            return {
                key,
                title: entry.stage,
                subtitle: FUNNEL_STAGE_SUBTITLES[key],
                ourRate: conversionRates[key] || '—',
                marketRate: getMarketReferenceRate(key),
            }
        })
    }, [chartData, conversionRates])

    const hasData = chartData.some((d) => d._total > 0)

    const handleSegmentClick = useCallback((segment: any, entity: string) => {
        if (!onPointClick) {
            return
        }
        onPointClick({
            chartType: 'stacked-funnel',
            labelKey: dataMode === 'by_campaign' ? 'campaignName' : 'visitSource',
            labelValue: entity,
            row: {
                ...(segment?.payload || {}),
                entity,
                sourceMode,
                dataMode,
            },
        })
    }, [onPointClick, dataMode, sourceMode])

    if (!hasData) {
        return (
            <AnalyticsChartWrapper>
                <div className="no-chart-data">No data available for funnel visualization</div>
            </AnalyticsChartWrapper>
        )
    }

    const yAxisWidth = isQuarterDensity ? 90 : isHalfDensity ? 110 : 150
    const yAxisTickFontSize = isQuarterDensity ? 11 : isHalfDensity ? 13 : 16
    const yAxisTickFontWeight = isQuarterDensity ? 500 : 600
    const chartLeftMargin = isQuarterDensity ? 4 : 16
    const chartRightMargin = isQuarterDensity ? 18 : 36
    const hasSourceTabs = dataMode === 'by_source' && !isQuarterDensity
    const chartTopMargin = isQuarterDensity ? 4 : isHalfDensity ? (hasSourceTabs ? 36 : 16) : (hasSourceTabs ? 26 : 16)
    const chartBottomMargin = isQuarterDensity ? 4 : 16
    const funnelBarSize = isQuarterDensity ? 28 : isHalfDensity ? 54 : 74
    const barCategoryGap = isQuarterDensity ? '8%' : isHalfDensity ? '4%' : '2%'
    const entityLabel = dataMode === 'by_campaign' ? 'Campaign' : 'Source'

    return (
        <AnalyticsChartWrapper>
            <div className={`analytics-stacked-funnel-chart ${densityClass}${onPointClick ? ' drillable' : ''}`}>
                <div className="stacked-funnel-columns">
                    {/* Left column: market reference summary */}
                    <div className="stacked-funnel-left">
                        <CACBenchmarkLegend
                            title="Funnel by Source"
                            rows={legendRows}
                            dense={densityClass !== 'density-full'}
                        />
                    </div>

                    {/* Right column: toggles + chart + entity filters */}
                    <div className="stacked-funnel-right">
                        {/* Source mode toggle — hidden entirely on quarter density to save space */}
                        {dataMode === 'by_source' && !isQuarterDensity && (
                            <div className="chart-toggles stacked-funnel-toggles">
                                <div className="control-group">
                                    <Toggle
                                        items={[
                                            { value: 'latest', text: isHalfDensity ? 'Latest' : 'Latest Source (Our Analytics)' },
                                            { value: 'initial', text: isHalfDensity ? 'Initial' : 'Initial Source (HubSpot)' },
                                        ]}
                                        value={sourceMode}
                                        onChange={(val: string) => setSourceMode(val as SourceMode)}
                                        toggleOff={false}
                                        arrowFocus={false}
                                        selectedClass="selected"
                                    />
                                </div>
                                {sourceMode === 'initial' && (
                                    <div className="stacked-funnel-note">
                                        <i className="fas fa-info-circle" />
                                        <span>Visits always use latest source (anonymous visitors have no HubSpot record)</span>
                                    </div>
                                )}
                            </div>
                        )}

                        <div className="stacked-funnel-chart-area">
                            <div className="chart-area-inner">
                            <ResponsiveContainer width="100%" height="100%">
                                <BarChart
                                    data={chartData}
                                    layout="vertical"
                                    barSize={funnelBarSize}
                                    barCategoryGap={barCategoryGap}
                                    margin={{ top: chartTopMargin, right: chartRightMargin, left: chartLeftMargin, bottom: chartBottomMargin }}
                                >
                                    <CartesianGrid strokeDasharray="3 3" horizontal={false} />
                                        <XAxis
                                            type="number"
                                            domain={[0, 100]}
                                            tick={false}
                                            axisLine={false}
                                            tickLine={false}
                                        />
                                        <YAxis
                                            type="category"
                                            dataKey="stage"
                                            width={yAxisWidth}
                                            tick={{ fontSize: yAxisTickFontSize, fontWeight: yAxisTickFontWeight }}
                                        />
                                        <Tooltip content={<CustomTooltip hoveredEntity={hoveredEntity} />} />
                                        {entities.map((entity, i) => (
                                            <Bar
                                                key={entity}
                                                dataKey={entity}
                                                name={entity}
                                                stackId="funnel"
                                                fill={entityColors[entity]}
                                                isAnimationActive={!transitioning}
                                                onClick={(segment: any) => handleSegmentClick(segment, entity)}
                                                onMouseEnter={() => setHoveredEntity(entity)}
                                                onMouseLeave={() => setHoveredEntity(null)}
                                                opacity={hoveredEntity !== null && hoveredEntity !== entity ? 0.5 : 1}
                                            >
                                                {i === entities.length - 1 && (
                                                    <LabelList
                                                        dataKey="_conversionLabel"
                                                        position="right"
                                                        fill="#666"
                                                        fontSize={15}
                                                        offset={4}
                                                    />
                                                )}
                                            </Bar>
                                        ))}
                                    </BarChart>
                                </ResponsiveContainer>
                            </div>
                        </div>

                        {/* Entity filter — compact dot swatches on small tiles, full checkboxes on full size */}
                        {allEntities.length > 1 && isQuarterDensity && (
                            <div className="stacked-funnel-entity-filters compact-swatches">
                                {allEntities.map((entity) => {
                                    const isVisible = !hiddenEntities.has(entity)
                                    return (
                                        <span
                                            key={entity}
                                            className={`compact-swatch-item ${isVisible ? 'active' : 'inactive'}`}
                                            onClick={() => toggleEntity(entity)}
                                            title={entity}
                                        >
                                            <span
                                                className="compact-swatch-dot"
                                                style={{ backgroundColor: isVisible ? entityColors[entity] : '#ccc' }}
                                            />
                                            <span className="compact-swatch-name">{entity}</span>
                                        </span>
                                    )
                                })}
                            </div>
                        )}
                        {allEntities.length > 1 && !isQuarterDensity && (
                            <div className={`stacked-funnel-entity-filters${isHalfDensity ? ' half-density' : ''}`}>
                                {hiddenEntities.size > 0 && (
                                    <button
                                        className="entity-filter-show-all-banner"
                                        onClick={showAllEntities}
                                        title="Show all sources"
                                    >
                                        <i className="fas fa-eye" /> Show All {entityLabel}s
                                    </button>
                                )}
                                <div className="entity-filter-header">
                                    <span className="entity-filter-label">Filter {entityLabel}s:</span>
                                    {hiddenEntities.size === 0 && (
                                        <span className="entity-filter-hint">Click to isolate</span>
                                    )}
                                </div>
                                <div className="entity-filter-checkboxes">
                                    {allEntities.map((entity) => {
                                        const isVisible = !hiddenEntities.has(entity)
                                        const count = entityTotals.get(entity) ?? 0
                                        return (
                                            <label
                                                key={entity}
                                                className={`entity-filter-checkbox ${isVisible ? 'active' : 'inactive'}`}
                                                onClick={(e) => {
                                                    e.preventDefault()
                                                    toggleEntity(entity)
                                                }}
                                            >
                                                <span
                                                    className="entity-filter-swatch"
                                                    style={{
                                                        backgroundColor: isVisible ? entityColors[entity] : '#ccc',
                                                    }}
                                                />
                                                <span className="entity-filter-name">{entity}</span>
                                                {!isHalfDensity && (
                                                    <span className="entity-filter-count">({count.toLocaleString()})</span>
                                                )}
                                            </label>
                                        )
                                    })}
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </AnalyticsChartWrapper>
    )
}
